import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { LandingProjeOlustur } from './LandingProjeOlustur.jsx';

const SWIPE_THRESHOLD_PX = 56;

function buildPromoSlides(assets, appBase) {
  return [
    {
      id: 'proje-olustur',
      label: 'Proje Oluştur',
      render: () => <LandingProjeOlustur assets={assets} appBase={appBase} />,
    },
  ];
}

export function LandingPromoTrack({ assets, appBase }) {
  const slides = useMemo(() => buildPromoSlides(assets, appBase), [assets, appBase]);
  const [index, setIndex] = useState(0);
  const startRef = useRef(null);
  const stageRef = useRef(null);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canSlide = slides.length > 1;
  const safeIndex = Math.max(0, Math.min(slides.length - 1, index));

  const goTo = (next) => {
    if (!canSlide) return;
    setIndex(Math.max(0, Math.min(slides.length - 1, next)));
  };

  useEffect(() => {
    if (index !== safeIndex) setIndex(safeIndex);
  }, [index, safeIndex]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const onWheel = (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!canSlide || Math.abs(event.deltaX) < 12) return;
      goTo(event.deltaX > 0 ? safeIndex + 1 : safeIndex - 1);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [canSlide, safeIndex, slides.length]);

  const onPointerDown = (event) => {
    if (!canSlide || event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('a, button, video, input, textarea')) return;
    startRef.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
  };

  const onPointerUp = (event) => {
    const start = startRef.current;
    startRef.current = null;
    if (!start || !canSlide) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;
    goTo(dx < 0 ? safeIndex + 1 : safeIndex - 1);
  };

  const onKeyDown = (event) => {
    if (!canSlide) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(safeIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(safeIndex - 1);
    }
  };

  return (
    <section class="pp-landing-promos" id="landing-promos" aria-label="Tanıtımlar">
      <div
        class={`pp-landing-promos__stage${canSlide ? ' is-slider' : ''}`}
        ref={stageRef}
        tabIndex={canSlide ? 0 : undefined}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          startRef.current = null;
        }}
        onKeyDown={onKeyDown}
      >
        {canSlide ? (
          <button
            type="button"
            class="pp-landing-promos__arrow pp-landing-promos__arrow--prev"
            aria-label="Önceki tanıtım"
            disabled={safeIndex === 0}
            onClick={() => goTo(safeIndex - 1)}
          >
            <i class="fas fa-chevron-left" aria-hidden="true" />
          </button>
        ) : null}

        <div class="pp-landing-promos__viewport">
          <div
            class={`pp-landing-promos__track${reduceMotion ? ' is-instant' : ''}`}
            style={{ transform: `translate3d(${-safeIndex * 100}%, 0, 0)` }}
          >
            {slides.map((slide) => (
              <div class="pp-landing-promos__slide" key={slide.id} data-promo={slide.id}>
                {slide.render()}
              </div>
            ))}
          </div>
        </div>

        {canSlide ? (
          <button
            type="button"
            class="pp-landing-promos__arrow pp-landing-promos__arrow--next"
            aria-label="Sonraki tanıtım"
            disabled={safeIndex === slides.length - 1}
            onClick={() => goTo(safeIndex + 1)}
          >
            <i class="fas fa-chevron-right" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {canSlide ? (
        <div class="pp-landing-promos__dots" role="tablist" aria-label="Tanıtım slaytları">
          {slides.map((slide, i) => (
            <button
              type="button"
              key={slide.id}
              class={`pp-landing-promos__dot${i === safeIndex ? ' is-active' : ''}`}
              role="tab"
              aria-selected={i === safeIndex ? 'true' : 'false'}
              aria-label={slide.label}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      ) : null}

      <div class="pp-landing-promos__footer">
        <div class="pp-landing-promos__footer-nav" role="group" aria-label="Sayfa gezinme">
          <button type="button" class="pp-landing-scroll-hint" data-landing-scroll="up">
            <span class="pp-landing-scroll-hint__label">Ana sayfa</span>
            <i class="fas fa-chevron-up" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="pp-landing-scroll-hint"
            data-landing-scroll="down"
            aria-label="Kredi paketleri bölümüne git"
          >
            <span class="pp-landing-scroll-hint__label">Kredi paketleri</span>
            <i class="fas fa-chevron-down" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
